import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Pagination from "../../../../components/ui/Pagination";
import {
  useBiometricLinks,
  useBiometricSystems,
  useSaveBiometricLinks,
} from "../../../../api/queries/attendanceQueries";
import { getApiErrorMessage } from "../../../../utils/apiError";
import { useCanModify } from "../../../../hooks/usePermissions";

const EMPTY = [];

/** Each employee's ID on a biometric device system; edit inline and save. */
const LinksTab = () => {
  const canModify = useCanModify("attendance", { company: true });
  const systemsData = useBiometricSystems().data;
  const systemInfo = systemsData || EMPTY;
  const systems = useMemo(
    () => (systemsData || EMPTY).map((s) => s.system),
    [systemsData],
  );
  const [system, setSystem] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [linked, setLinked] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [edits, setEdits] = useState({}); // { employeeId: externalId | "" }
  const save = useSaveBiometricLinks();

  useEffect(() => {
    if (!system && systems.length) setSystem(systems[0]);
  }, [system, systems]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    setPage(1);
  }, [system, debounced, linked]);

  const query = useBiometricLinks(
    {
      system,
      search: debounced || undefined,
      linked: linked || undefined,
      page,
      limit,
    },
    { enabled: !!system },
  );
  const rows = query.data?.data || EMPTY;
  const changed = Object.keys(edits).length;

  const switchSystem = (s) => {
    if (changed && !window.confirm("Discard unsaved changes?")) return;
    setEdits({});
    setSystem(s);
  };

  const submit = () =>
    save.mutate(
      {
        system,
        links: Object.entries(edits).map(([employeeId, externalId]) => ({
          employeeId,
          externalId: externalId.trim() || null,
        })),
      },
      {
        onSuccess: () => {
          toast.success(`${changed} ID(s) saved.`);
          setEdits({});
        },
        onError: (err) =>
          toast.error(
            getApiErrorMessage(
              err,
              err?.response?.status === 409
                ? "One of these IDs already belongs to another employee."
                : "Could not save.",
            ),
          ),
      },
    );

  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div>
          <label htmlFor="bio-system" className="profile-label">
            Device / software
          </label>
          <input
            id="bio-system"
            className="form-control"
            list="bio-system-list"
            value={system}
            onChange={(e) => switchSystem(e.target.value)}
            placeholder="e.g. ZKTeco"
          />
          <datalist id="bio-system-list">
            {systemInfo.map((s) => (
              <option
                key={s.system}
                value={s.system}
              >{`${s.linkedEmployees ?? 0} linked`}</option>
            ))}
          </datalist>
        </div>
        <div>
          <label htmlFor="bio-search" className="profile-label">
            Search
          </label>
          <input
            id="bio-search"
            type="search"
            className="form-control"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, employee no. or device ID"
          />
        </div>
        <div>
          <label htmlFor="bio-linked" className="profile-label">
            Show
          </label>
          <select
            id="bio-linked"
            className="form-select"
            value={linked}
            onChange={(e) => setLinked(e.target.value)}
          >
            <option value="">Everyone</option>
            <option value="true">Linked</option>
            <option value="false">Not linked</option>
          </select>
        </div>
        <div className="ms-auto d-flex gap-2">
          {changed > 0 && (
            <button
              type="button"
              className="btn btn-outline-secondary profile-btn"
              onClick={() => setEdits({})}
              disabled={save.isPending}
            >
              Discard
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={!changed || save.isPending || !system}
          >
            {save.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Save {changed > 0 ? `(${changed})` : ""}
          </button>
        </div>
      </div>

      {!system ? (
        <div className="att-card approval-empty">
          <i className="bi bi-fingerprint"></i>Enter the device system to see
          its IDs.
        </div>
      ) : (
        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table mb-0 att-table">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3">Employee</th>
                  <th className="py-3">Employee no.</th>
                  <th className="py-3 pe-4">ID on {system}</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {rows.map((r) => {
                  const value = edits[r.employeeId] ?? r.externalId ?? "";
                  const dirty = edits[r.employeeId] !== undefined;
                  return (
                    <tr key={r.employeeId}>
                      <td className="ps-4 fw-semibold">{r.name}</td>
                      <td>{r.employeeNumber || "—"}</td>
                      <td className="pe-4">
                        <input
                          className={`form-control form-control-sm font-monospace bio-link-input ${dirty ? "is-dirty" : ""}`}
                          value={value}
                          onChange={(e) =>
                            setEdits((x) => {
                              const next = { ...x };
                              if (e.target.value === (r.externalId ?? ""))
                                delete next[r.employeeId];
                              else next[r.employeeId] = e.target.value;
                              return next;
                            })
                          }
                          placeholder="Not linked"
                          readOnly={!canModify}
                          aria-label={`Device ID for ${r.name}`}
                        />
                      </td>
                    </tr>
                  );
                })}
                {!query.isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={3} className="approval-empty">
                      No employees found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {query.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
          {query.isError && (
            <div className="approval-empty text-danger">
              {getApiErrorMessage(query.error, "Could not load the IDs.")}
            </div>
          )}
          <Pagination
            page={page}
            limit={limit}
            total={query.data?.total || 0}
            onPageChange={setPage}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        </div>
      )}
    </>
  );
};

export default LinksTab;
